// src/components/Hero.jsx
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Maximize, Minimize } from "lucide-react";
import content from "../data/content.json";
import { useSiteHome } from "../hooks/useSiteHome";
import { normalizeHeroMedios } from "../lib/youtubeIds";
import HeroYoutubePlayer from "./HeroYoutubePlayer";
import MediosVolumeControls from "./MediosVolumeControls";
import { useHeroChromeIdle } from "./HeroIdleChrome";
import { useMediosChannelAudio } from "../hooks/useMediosChannelAudio";
import { useCookieConsentOptional } from "../context/CookieConsentContext";

function enterFullscreen(el) {
  if (!el) return Promise.resolve();
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  if (el.webkitEnterFullscreen) return Promise.resolve(el.webkitEnterFullscreen());
  return Promise.reject(new Error("fullscreen"));
}

function exitFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  return Promise.resolve();
}

function isFullscreen() {
  return Boolean(document.fullscreenElement || document.webkitFullscreenElement);
}

const Hero = ({ isReady }) => {
  const { data } = useSiteHome();
  const hero = data?.hero || content.hero;
  const medios = normalizeHeroMedios(hero.medios);
  const useEmbed = medios.enHero && medios.clips.length > 0;
  const cookieConsent = useCookieConsentOptional();
  const useThirdPartyEmbed = useEmbed && Boolean(cookieConsent?.thirdPartyAllowed);
  const { muted: ytMuted, volume: ytVolume, setMutedExclusive, setVolumeLevel } =
    useMediosChannelAudio("hero");
  const [ytCaptions, setYtCaptions] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const chromeIdle = useHeroChromeIdle();
  const stageRef = useRef(null);

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen());
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (isFullscreen()) await exitFullscreen();
      else await enterFullscreen(stageRef.current);
    } catch {
      /* iOS / política del navegador */
    }
  };

  const controlBtn =
    "pointer-events-auto inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-black/60 text-white backdrop-blur-md hover:bg-black/80 p-2.5 sm:px-3.5 sm:py-2";

  return (
    <section
      id="inicio"
      className="relative overflow-hidden bg-[#05190f] text-white"
    >
      <div className="relative flex flex-col min-h-[100svh] md:min-h-[92vh] w-full">
        <div ref={stageRef} className="hero-stage absolute inset-0 z-0 bg-[#05190f]">
          {useThirdPartyEmbed ? (
            <HeroYoutubePlayer
              clips={medios.clips}
              cropTopPx={medios.cropTopPx}
              cropBottomPx={medios.cropBottomPx}
              mensajeRail={medios.mensajeRail}
              muted={ytMuted}
              volume={ytVolume}
              captions={ytCaptions}
              fallbackPoster={hero.posterUrl}
              fallbackVideo={hero.videoUrl}
            />
          ) : (
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={hero.posterUrl}
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={hero.videoUrl} type="video/mp4" />
            </video>
          )}
          <div
            className={`hero-scrim absolute inset-0 bg-gradient-to-b from-[#05190f]/80 via-[#05190f]/25 to-[#05190f]/95 z-10 pointer-events-none transition-opacity duration-500 ${
              chromeIdle ? "opacity-20" : "opacity-100"
            }`}
          />

          {useThirdPartyEmbed ? (
            <div
              className={`absolute right-4 top-[calc(var(--nav-h)+0.85rem)] z-40 flex flex-col items-end gap-2 md:right-8 transition-opacity duration-300 ${
                chromeIdle ? "pointer-events-none opacity-0" : "opacity-100"
              }`}
            >
              <MediosVolumeControls
                muted={ytMuted}
                volume={ytVolume}
                onMuteToggle={() => setMutedExclusive(!ytMuted)}
                onVolumeLevel={setVolumeLevel}
                buttonClass={controlBtn}
              />
              <button
                type="button"
                onClick={() => void toggleFullscreen()}
                className={controlBtn}
                aria-pressed={fullscreen}
                aria-label={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
              >
                {fullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                <span className="hidden sm:inline text-xs font-bold uppercase tracking-widest">
                  {fullscreen ? "Salir" : "Pantalla completa"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setYtCaptions((v) => !v)}
                className={`${controlBtn} min-w-[42px] ${ytCaptions ? "border-brand-accent/70 bg-brand-accent/20" : ""}`}
                aria-pressed={ytCaptions}
                aria-label={ytCaptions ? "Ocultar subtítulos" : "Mostrar subtítulos"}
                title={ytCaptions ? "Ocultar subtítulos (CC)" : "Mostrar subtítulos (CC)"}
              >
                <span className={`text-[11px] font-black tracking-[0.12em] ${ytCaptions ? "text-brand-accent" : "text-white"}`}>
                  CC
                </span>
                <span className="hidden sm:inline text-xs font-bold uppercase tracking-widest">
                  {ytCaptions ? "Subtítulos" : "Subtítulos"}
                </span>
              </button>
            </div>
          ) : null}
          <div className="hero-med-frame pointer-events-none" aria-hidden>
            <span className="hero-med-frame__c hero-med-frame__c--tl" />
            <span className="hero-med-frame__c hero-med-frame__c--tr" />
            <span className="hero-med-frame__c hero-med-frame__c--bl" />
            <span className="hero-med-frame__c hero-med-frame__c--br" />
          </div>
        </div>

        <div className="container mx-auto px-6 relative z-20 pointer-events-none flex flex-col justify-center
                        pt-[88px] pb-28 md:pt-0 md:pb-0 md:justify-center
                        flex-1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isReady && !chromeIdle ? { opacity: 1, y: 0 } : { opacity: 0, y: chromeIdle ? 8 : 0 }}
            transition={{ duration: chromeIdle ? 0.4 : 0.25, delay: !isReady ? 0.4 : 0 }}
            className={`max-w-[720px] ${chromeIdle ? "pointer-events-none" : ""}`}
          >
            <header>
              <div className="inline-flex items-center gap-2 bg-brand-accent/20 backdrop-blur-md border border-brand-accent/30 px-3 py-1 rounded-full text-[0.65rem] font-body font-bold uppercase tracking-[0.2em] text-brand-accent mb-5 md:mb-6">
                {hero.badge}
              </div>

              <h1
                id="hero-title"
                className="font-display leading-[0.95] font-black mb-5 md:mb-6 tracking-tighter text-white"
                style={{ fontSize: "clamp(1.8rem, 9vw, 3.5rem)" }}
              >
                {hero.title} <br />
                <span className="text-brand-accent italic font-semibold">
                  {hero.titleHighlight}
                </span>
              </h1>
            </header>

            <p className="font-body text-base md:text-xl text-white/80 mb-8 md:mb-10 max-w-[680px] font-light leading-relaxed">
              {hero.subtitle}
            </p>

            <div className="pointer-events-auto flex flex-wrap gap-3 md:gap-4">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/contacto"
                  className="inline-block bg-brand-accent text-brand-dark px-6 py-3 md:px-7 md:py-4 rounded-full font-bold text-sm shadow-lg transition-all"
                >
                  {hero.primaryCTA?.label || "Evaluar mi empresa"}
                </Link>
              </motion.div>

              <Link
                to="/investigacion"
                className="group flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 md:px-7 md:py-4 rounded-full font-bold text-sm hover:bg-white/20 transition-all"
              >
                {hero.secondaryCTA?.label || "Ver investigaciones"}
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  →
                </motion.span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
